
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TreeDeciduous, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import DeforestationTab from './panels/DeforestationTab';
import LandLossTab from './panels/LandLossTab';
import DesertificationTab from './panels/DesertificationTab';
import SpeciesTab from './panels/SpeciesTab';
import { CriticalityBadge } from './ui/StatusBadges';
import { EnvironmentalData, Region, fetchRegionById } from '@/services/environmentalDataService';

interface ComparisonPanelProps {
  regionId: string;
  data: EnvironmentalData;
}

const ComparisonPanel: React.FC<ComparisonPanelProps> = ({ regionId, data }) => {
  const [activeTab, setActiveTab] = useState('deforestation');
  const [region, setRegion] = useState<Region | null>(null);
  
  useEffect(() => {
    const loadRegion = async () => {
      try {
        const regionData = await fetchRegionById(regionId);
        setRegion(regionData);
      } catch (error) {
        console.error('Error loading region:', error);
      }
    };
    
    loadRegion();
  }, [regionId]);

  if (!region || !data) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Region data not found</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Unable to load environmental data for this region.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex flex-col space-y-1">
          <CardTitle className="text-xl text-primary">{region.name}</CardTitle>
          <p className="text-sm text-muted-foreground">{region.country} • {region.continent}</p>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <div className="flex flex-col p-3 bg-primary/5 rounded-md border border-border text-center">
            <span className="text-sm text-muted-foreground">Risk Level</span>
            <div className="flex justify-center mt-1">
              <CriticalityBadge level={data.desertification.riskLevel} />
            </div>
          </div>
          <div className="flex flex-col p-3 bg-primary/5 rounded-md border border-border text-center">
            <span className="text-sm text-muted-foreground">Endangered Species</span>
            <span className="text-xl font-medium">{data.endangeredSpecies.count}</span>
          </div>
        </div>
      </CardHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid grid-cols-4 w-[90%] mx-auto mt-2">
          <TabsTrigger value="deforestation" className="text-xs sm:text-sm">
            <TreeDeciduous className="h-4 w-4 mr-1 hidden sm:inline" />
            Deforest
          </TabsTrigger>
          <TabsTrigger value="landLoss" className="text-xs sm:text-sm">Land</TabsTrigger>
          <TabsTrigger value="desertification" className="text-xs sm:text-sm">Desert</TabsTrigger>
          <TabsTrigger value="species" className="text-xs sm:text-sm">
            <AlertTriangle className="h-4 w-4 mr-1 hidden sm:inline" />
            Species
          </TabsTrigger>
        </TabsList>

        <div className="px-6 py-4">
          <TabsContent value="deforestation" className="mt-0">
            <DeforestationTab data={data} />
          </TabsContent>

          <TabsContent value="landLoss" className="mt-0">
            <LandLossTab data={data} />
          </TabsContent>

          <TabsContent value="desertification" className="mt-0">
            <DesertificationTab data={data} />
          </TabsContent>

          <TabsContent value="species" className="mt-0">
            <SpeciesTab data={data} />
          </TabsContent>
        </div>
      </Tabs>
    </Card>
  );
};

export default ComparisonPanel;
